/**
 * TeamsManagementPanel — app-level Settings UI for project groups ("Teams").
 *
 * Teams are the user-defined org grouping layer above projects (e.g.
 * "Advantage", "Unveiled", "Personal"). This panel lists them, lets you
 * add / rename / recolor / delete and reorder them. A project is assigned to a
 * team from the project list (Projects panel / mobile switcher) — not here — so
 * this panel only manages the team DEFINITIONS.
 *
 * Internally the feature is named "group" to avoid colliding with the Microsoft
 * Teams chat integration (Settings → Microsoft Teams). Backing surface:
 * `backend.groups.*` (core `group.*` IPC), mirrored client-side by groupStore.
 */
import { useEffect, useState } from 'react';
import { Users, Plus, Trash2, ChevronUp, ChevronDown, Check, X } from 'lucide-react';
import { useGroupStore } from '@/stores/groupStore';
import { useProjectStore } from '@/stores/projectStore';
import { getProjectGroupId } from '@/lib/projectSettings';
import { logger } from '@/lib/logger';

const TAG = 'TeamsManagementPanel';

/** Preset accent colors for a team. Tailwind-ish hues that read in both themes. */
const TEAM_COLORS = [
  '#8b5cf6', // violet
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#ec4899', // pink
  '#14b8a6', // teal
  '#64748b', // slate
];

export default function TeamsManagementPanel() {
  const groups = useGroupStore((s) => s.groups);
  const loadGroups = useGroupStore((s) => s.loadGroups);
  const createGroup = useGroupStore((s) => s.createGroup);
  const updateGroup = useGroupStore((s) => s.updateGroup);
  const deleteGroup = useGroupStore((s) => s.deleteGroup);
  const reorderGroups = useGroupStore((s) => s.reorderGroups);
  const projects = useProjectStore((s) => s.projects);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(TEAM_COLORS[0]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState<string | null>(null);

  useEffect(() => { void loadGroups(); }, [loadGroups]);

  // Project count per team, so each row shows how many projects it holds.
  const countFor = (groupId: string) =>
    projects.filter((p) => p.status !== 'archived' && getProjectGroupId(p.settings) === groupId).length;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      logger.warn(TAG, 'action failed', e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const add = () => run(async () => {
    const name = newName.trim();
    if (!name) { setError('Team name is required.'); return; }
    await createGroup(name, newColor);
    setNewName('');
    setNewColor(TEAM_COLORS[0]);
    setAdding(false);
  });

  const saveEdit = (id: string) => run(async () => {
    const name = editName.trim();
    if (!name) { setError('Team name is required.'); return; }
    await updateGroup(id, { name, color: editColor });
    setEditingId(null);
  });

  const move = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= groups.length) return;
    const ids = groups.map((g) => g.id);
    [ids[index], ids[next]] = [ids[next], ids[index]];
    void run(() => reorderGroups(ids));
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-4 h-4 text-primary" />
          <h3 className="font-medium">Teams</h3>
        </div>
        <p className="text-sm opacity-70">
          Group your projects by team (e.g. Advantage, Unveiled, Personal). Each project
          can belong to one team; assign a project from its menu in the Projects list, or
          drag it onto a team. Projects without a team show under <em>Ungrouped</em>.
        </p>
      </div>

      {error && (
        <div className="alert alert-warning text-sm py-2">
          <X className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {groups.length === 0 && !adding && (
        <div className="text-sm opacity-60 border border-dashed border-base-300 rounded-lg p-4 text-center">
          No teams yet. Add one to start organizing your projects by client or org.
        </div>
      )}

      <div className="space-y-2">
        {groups.map((g, index) => {
          const editing = editingId === g.id;
          return (
            <div key={g.id} className="border border-base-300 rounded-lg p-3 flex items-center justify-between gap-3">
              {editing ? (
                <div className="flex-1 min-w-0 space-y-2">
                  <input
                    className="input input-bordered input-sm w-full"
                    value={editName}
                    autoFocus
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(g.id); if (e.key === 'Escape') setEditingId(null); }}
                  />
                  <ColorPicker value={editColor} onChange={setEditColor} />
                </div>
              ) : (
                <div className="flex items-center gap-2.5 min-w-0">
                  <span
                    className="w-3 h-3 rounded-full shrink-0 border border-base-content/20"
                    style={{ backgroundColor: g.color ?? 'transparent' }}
                  />
                  <span className="font-medium truncate">{g.name}</span>
                  <span className="text-xs opacity-50">{countFor(g.id)} project{countFor(g.id) === 1 ? '' : 's'}</span>
                </div>
              )}

              <div className="flex items-center gap-1 shrink-0">
                {editing ? (
                  <>
                    <button className="btn btn-primary btn-xs gap-1" disabled={busy} onClick={() => saveEdit(g.id)}>
                      <Check className="w-3.5 h-3.5" /> Save
                    </button>
                    <button className="btn btn-ghost btn-xs" disabled={busy} onClick={() => setEditingId(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button className="btn btn-ghost btn-xs px-1" disabled={busy || index === 0} onClick={() => move(index, -1)} title="Move up">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button className="btn btn-ghost btn-xs px-1" disabled={busy || index === groups.length - 1} onClick={() => move(index, 1)} title="Move down">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      className="btn btn-ghost btn-xs"
                      disabled={busy}
                      onClick={() => { setEditingId(g.id); setEditName(g.name); setEditColor(g.color ?? null); }}
                      title="Rename / recolor"
                    >
                      Edit
                    </button>
                    <button className="btn btn-ghost btn-xs text-error" disabled={busy} onClick={() => run(() => deleteGroup(g.id))} title="Delete team (its projects become Ungrouped)">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {adding ? (
        <div className="border border-base-300 rounded-lg p-3 space-y-3">
          <div className="font-medium text-sm">Add team</div>
          <input
            className="input input-bordered input-sm w-full"
            placeholder="Advantage"
            value={newName}
            autoFocus
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); if (e.key === 'Escape') setAdding(false); }}
          />
          <ColorPicker value={newColor} onChange={(c) => setNewColor(c ?? TEAM_COLORS[0])} />
          <div className="flex items-center gap-2">
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={add}>
              {busy ? <span className="loading loading-spinner loading-xs" /> : 'Add'}
            </button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => { setAdding(false); setNewName(''); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn btn-outline btn-sm gap-1" onClick={() => setAdding(true)}>
          <Plus className="w-4 h-4" /> Add team
        </button>
      )}
    </div>
  );
}

/** A row of preset color swatches; the selected one gets a ring. */
function ColorPicker({ value, onChange }: { value: string | null; onChange: (c: string | null) => void }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {TEAM_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          className={`w-6 h-6 rounded-full border transition-transform ${value === c ? 'ring-2 ring-offset-2 ring-offset-base-100 ring-base-content scale-110' : 'border-base-content/20'}`}
          style={{ backgroundColor: c }}
          onClick={() => onChange(c)}
          title={c}
        />
      ))}
    </div>
  );
}
